/* One clock for the whole application, and it says which one it is.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * Before this file the repo mixed clocks inside single widgets, and every
 * instance was invisible until you put a ruler on it:
 *
 *   - DayTimeline positioned a clip marker with getUTCHours() and labelled the
 *     same marker with fmtTime(), which is local. In Norway those are two hours
 *     apart, so the dot sat two hours from where its own tooltip said it was.
 *   - The calendar bucketed by UTC day; the clips table under it printed local.
 *     A clip at 22:30 UTC on the 11th appeared on the 11th and read "12 Aug,
 *     00:30".
 *   - The same detections read local on the Sound tab and UTC in EventList.
 *
 * None of these were wrong on their own. Each surface had picked the clock that
 * suited it and said nothing, and the disagreements only appear when two of
 * them are on screen together -- which, on a page that has to show a live
 * player and an archive of the same day, is always.
 *
 * ---------------------------------------------------------------------------
 * LOCAL BY DEFAULT, AND THE ZONE IS ALWAYS NAMED
 *
 * Calendar.tsx argues for UTC:
 *
 *   "a calendar that silently shifts by the viewer's timezone would put a clip
 *    on the wrong day for anyone east of Greenwich -- which is everyone here."
 *
 * That is right about *silently*, and it is an argument for naming the zone
 * rather than for choosing UTC. A farmer who saw something at three in the
 * morning means three in the morning where they were standing. So: local wins
 * by default, the abbreviation is printed beside every time, and one switch
 * moves everything at once. Nothing shifts without saying so.
 *
 * A file that leaves this building keeps UTC in its name whatever the screen
 * shows. A download outlives the browser that made it, and the person who opens
 * it a year later has no way to know which zone the tab was in.
 *
 * ---------------------------------------------------------------------------
 * THE DAY BOUNDARY IS A CORRECTNESS PROBLEM, NOT A PREFERENCE
 *
 * The archive endpoints bucket by UTC date: `/day?date=2026-08-12` means the
 * UTC day. Norway is UTC+2 in summer, so 01:00 local on the 13th lives in the
 * 12th's bucket. Ask for "yesterday" in local time and one request cannot
 * answer it -- the local day straddles two UTC days.
 *
 * `utcDaysFor` is the whole of the fix: it returns the one or two UTC dates
 * that cover a local day, the caller fetches both and merges. Two requests, no
 * backend change, and the strip a farmer reads starts at midnight where they
 * are standing rather than at 02:00.
 */
import { useSyncExternalStore } from 'react'

export type Zone = 'local' | 'utc'

const KEY = 'phoniq.tz'

function initial(): Zone {
  try {
    // Only 'utc' is stored as a departure from the default. An unreadable or
    // absent value means local, which is also what a first-time visitor gets.
    if (localStorage.getItem(KEY) === 'utc') return 'utc'
  } catch {
    /* private mode, or storage disabled: local is the right answer anyway */
  }
  return 'local'
}

let zone: Zone = initial()
const listeners = new Set<() => void>()

/** The zone in force, readable from plain modules as well as components. */
export function currentZone(): Zone {
  return zone
}

export function setZone(z: Zone): void {
  if (z === zone) return
  zone = z
  try {
    localStorage.setItem(KEY, z)
  } catch {
    /* not being able to remember the choice is not a reason to refuse it */
  }
  listeners.forEach((fn) => fn())
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** `const zone = useZone()`. Re-renders on a switch. */
export function useZone(): Zone {
  return useSyncExternalStore(subscribe, currentZone, () => 'local' as Zone)
}

/* --------------------------------------------------------------- formatting */

/** The `timeZone` option to hand Intl, or undefined to mean "wherever we are". */
function tz(): string | undefined {
  return zone === 'utc' ? 'UTC' : undefined
}

const fmt = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tz() })

/** What to print beside a time so it is not ambiguous: `CEST`, or `UTC`. */
export function zoneLabel(epochS: number = Date.now() / 1000): string {
  if (zone === 'utc') return 'UTC'
  const parts = fmt({ timeZoneName: 'short' }).formatToParts(new Date(epochS * 1000))
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
}

/** `14:32`. The zone is deliberately NOT appended: a strip of axis labels does
 *  not want it eleven times. Print it once, near the readout. */
export function clock(epochS: number, seconds = false): string {
  return fmt({
    hour: '2-digit', minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
  }).format(new Date(epochS * 1000))
}

/** `12. aug`. */
export function dayLabel(epochS: number): string {
  return fmt({ day: 'numeric', month: 'short' }).format(new Date(epochS * 1000))
}

/** `12. aug 03:14:22 CEST` -- the readout, where there is room to be
 *  unambiguous and where being unambiguous is the point. */
export function stampFull(epochS: number, seconds = true): string {
  return `${dayLabel(epochS)} ${clock(epochS, seconds)} ${zoneLabel(epochS)}`
}

/* ----------------------------------------------------------------- day maths
 *
 * Everything below works in whichever zone is in force. `getHours()` is the
 * browser's zone and `getUTCHours()` is UTC, so the two are switched between
 * rather than one being used with a correction applied -- an offset added by
 * hand is what breaks twice a year.
 */

const pad = (n: number) => String(n).padStart(2, '0')

/** `YYYY-MM-DD` for the day this instant falls in, in the active zone. */
export function dayKey(epochS: number): string {
  const d = new Date(epochS * 1000)
  return zone === 'utc'
    ? `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
    : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Hour of the day, 0-23, in the active zone. What a marker's position on a
 *  24-hour strip is drawn from -- and what its label must agree with. */
export function hourFraction(epochS: number): number {
  const d = new Date(epochS * 1000)
  const [h, m, s] = zone === 'utc'
    ? [d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
    : [d.getHours(), d.getMinutes(), d.getSeconds()]
  return (h * 3600 + m * 60 + s) / 86400
}

/** Midnight-to-midnight for a `YYYY-MM-DD`, in the active zone, as epoch
 *  seconds. The end is the next midnight, so the range is half-open. */
export function dayBounds(day: string): [number, number] {
  const [y, m, d] = day.split('-').map(Number)
  if (zone === 'utc') {
    const t0 = Date.UTC(y!, m! - 1, d!) / 1000
    return [t0, t0 + 86400]
  }
  // Local: construct through the Date constructor, which resolves the zone --
  // including the day in March that has 23 hours and the one in October that
  // has 25. Adding 86400 to the start would be wrong on both of them.
  const start = new Date(y!, m! - 1, d!, 0, 0, 0, 0)
  const end = new Date(y!, m! - 1, d! + 1, 0, 0, 0, 0)
  return [start.getTime() / 1000, end.getTime() / 1000]
}

/** Today, in the active zone. */
export function today(): string {
  return dayKey(Date.now() / 1000)
}

/** Move a `YYYY-MM-DD` by whole days. Calendar arithmetic, not clock
 *  arithmetic: it must not drift across a daylight-saving boundary. */
export function shiftDay(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const t = new Date(Date.UTC(y!, m! - 1, d! + days))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

/** The UTC dates that must be fetched to cover one day in the active zone.
 *
 *  One when the zone is UTC or the offset happens to be zero; two otherwise,
 *  and which two depends on the sign of the offset -- east of Greenwich a local
 *  day starts in the previous UTC day, west of it, it ends in the next.
 *
 *  This is the request-level half of the day-boundary fix. The response-level
 *  half is that the caller must then filter to `dayBounds(day)` rather than
 *  drawing everything both requests returned.
 */
export function utcDaysFor(day: string): string[] {
  if (zone === 'utc') return [day]
  const [t0, t1] = dayBounds(day)
  const first = new Date(t0 * 1000)
  // t1 is the next midnight and therefore exclusive; step back a second so a
  // local day that ends exactly on a UTC midnight does not pull in a third day
  // that contains none of it.
  const last = new Date((t1 - 1) * 1000)
  const key = (d: Date) =>
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
  const a = key(first)
  const b = key(last)
  return a === b ? [a] : [a, b]
}
